import React, { useState } from 'react';
import './styles/AccountCreation.css';
import { Link } from 'react-router-dom';

export default function AccountCreation() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false
  });
  
  const [errors, setErrors] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validatePassword = (password) => {
    const minLength = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    
    return {
      isValid: minLength && hasUpperCase && hasLowerCase && hasNumbers && hasSpecialChar,
      requirements: {
        minLength,
        hasUpperCase,
        hasLowerCase,
        hasNumbers,
        hasSpecialChar
      }
    };
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    const passwordValidation = validatePassword(formData.password);
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (!passwordValidation.isValid) {
      newErrors.password = 'Password does not meet requirements';
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = () => {
    if (validateForm()) {
      setIsSubmitting(true);
      // Simulate API call
      setTimeout(() => {
        setIsSubmitting(false);
        setShowModal(true);
      }, 1000);
    }
  };

  const handleVerificationSubmit = () => {
    if (verificationCode.length === 6) {
      // Simulate verification
      setTimeout(() => {
        alert('Account created successfully!');
        setShowModal(false);
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          password: '',
          confirmPassword: '',
          agreeToTerms: false
        });
        setVerificationCode('');
      }, 500);
    }
  };

  const passwordValidation = validatePassword(formData.password);

  return (
    <div className="account-creation-container">
      <div className="account-form-card">
        <div className="form-header">
          <h1 className="form-title">Create Account</h1>
          <p className="form-subtitle">Join us today and get started</p>
        </div>

        <div className="form-fields">
          <div className="name-fields">
            <div className="field-group">
              <label className="field-label">First Name</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className={`field-input ${errors.firstName ? 'field-input--error' : ''}`}
                placeholder="John"
              />
              {errors.firstName && (
                <span className="field-error">{errors.firstName}</span>
              )}
            </div>
            
            <div className="field-group">
              <label className="field-label">Last Name</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className={`field-input ${errors.lastName ? 'field-input--error' : ''}`}
                placeholder="Doe"
              />
              {errors.lastName && (
                <span className="field-error">{errors.lastName}</span>
              )}
            </div>
          </div>

          <div className="field-group">
            <label className="field-label">Email Address</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`field-input ${errors.email ? 'field-input--error' : ''}`}
              placeholder="john.doe@example.com"
            />
            {errors.email && (
              <span className="field-error">{errors.email}</span>
            )}
          </div>

          <div className="field-group">
            <label className="field-label">Password</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className={`field-input ${errors.password ? 'field-input--error' : ''}`}
              placeholder="Enter a strong password"
            />
            {errors.password && (
              <span className="field-error">{errors.password}</span>
            )}
            
            {formData.password && (
              <div className="password-requirements">
                <p className="requirements-title">Password Requirements:</p>
                <div>
                  <div className={`requirement-item ${passwordValidation.requirements.minLength ? 'requirement-item--valid' : 'requirement-item--invalid'}`}>
                    ✓ At least 8 characters
                  </div>
                  <div className={`requirement-item ${passwordValidation.requirements.hasUpperCase ? 'requirement-item--valid' : 'requirement-item--invalid'}`}>
                    ✓ One uppercase letter
                  </div>
                  <div className={`requirement-item ${passwordValidation.requirements.hasLowerCase ? 'requirement-item--valid' : 'requirement-item--invalid'}`}>
                    ✓ One lowercase letter
                  </div>
                  <div className={`requirement-item ${passwordValidation.requirements.hasNumbers ? 'requirement-item--valid' : 'requirement-item--invalid'}`}>
                    ✓ One number
                  </div>
                  <div className={`requirement-item ${passwordValidation.requirements.hasSpecialChar ? 'requirement-item--valid' : 'requirement-item--invalid'}`}>
                    ✓ One special character
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="field-group">
            <label className="field-label">Confirm Password</label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className={`field-input ${errors.confirmPassword ? 'field-input--error' : ''}`}
              placeholder="Confirm your password"
            />
            {errors.confirmPassword && (
              <span className="field-error">{errors.confirmPassword}</span>
            )}
          </div>

          <div className="field-group">
            <div className="terms-group">
              <input
                type="checkbox"
                name="agreeToTerms"
                checked={formData.agreeToTerms}
                onChange={handleInputChange}
                className="terms-checkbox"
              />
              <span className="terms-text">
                I agree to the{' '}
                <a href="#" className="terms-link">Terms and Conditions</a>
                {' '}and{' '}
                <a href="#" className="terms-link">Privacy Policy</a>
              </span>
            </div>
            {errors.agreeToTerms && (
              <span className="field-error">{errors.agreeToTerms}</span>
            )}
          </div>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="submit-button"
          >
            {isSubmitting ? 'Creating Account...' : 'Create Account'}
          </button>
        </div>

        <div className="signin-link">
          Already have an account?{' '}
          <a href="#">Sign in</a>
        </div>
      </div>

      {/* Email Verification Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="verify-modal-header">
              <h2 className="modal-title">Verify Your Email</h2>
              <p className="modal-description">
                We've sent a 6-digit verification code to<br />
                <span className="modal-email">{formData.email}</span>
              </p>
            </div>

            <div className="verification-form">
              <div className="field-group">
                <label className="field-label">Verification Code</label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="verification-input"
                  placeholder="000000"
                  maxLength={6}
                />
                <p className="verification-note">
                  For demo purposes, enter any 6-digit code
                </p>
              </div>

              <div className="modal-buttons">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="modal-button modal-button--cancel"
                >
                  Cancel
                </button>
                <Link  
                  to='/community/account' 
                  className="modal-button modal-button--verify"
                >
                  Verify
                </Link>
              </div>
            </div>

            <div className="resend-link">
              Didn't receive the code?{' '}
              <button>Resend</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
